import { registerMockAPI } from '~/mocks/mockApis';

import { useAPI, useAsyncHandler } from '~/hooks/common';

import { API_ENDPOINT } from '~/constants';

import type {
  TypeExchangeDetailData,
  TypeGetExchangeOrdersExchangeReceiptIdParams,
  TypeGetExchangeOrdersExchangeReceiptIdResponse,
} from '~/types/claim.types';
import type { TypeErrorResponseData } from '~/types/common.types';

/**
 * 상품 코드가 같은 단위끼리 묶어서 포맷팅하는 함수
 * - 상품코드 기준으로 그룹핑
 * - 검수 단위 별 정보는 inspectionDetails 배열에 추가
 */
const formatExchangeDetailData = (
  data: TypeGetExchangeOrdersExchangeReceiptIdResponse['data']
): TypeExchangeDetailData => {
  const {
    returnOrder: { returnGoodsInspections },
  } = data;

  const groupedByGoodsCode: Record<string, TypeExchangeDetailData['returnOrder']['returnGoodsInspections'][number]> =
    {};

  returnGoodsInspections.forEach((inspection) => {
    const {
      goodsCode,
      goodsName,
      returnQuantity,
      returnReason,
      inspectionQuantity,
      inboundStatus,
      inspectionStatus,
      inspectionImageUrls,
      inspectionAt,
      unlistedYn,
    } = inspection;

    if (!groupedByGoodsCode[goodsCode]) {
      groupedByGoodsCode[goodsCode] = {
        goodsCode,
        goodsName,
        returnQuantity,
        returnReason,
        inspectionDetails: [],
      };
    }

    // 검수 단위 별 정보 추가
    groupedByGoodsCode[goodsCode].inspectionDetails.push({
      inspectionQuantity,
      inboundStatus,
      inspectionStatus,
      inspectionImageUrls,
      inspectionAt,
      unlistedYn,
    });
  });

  const formattedReturnGoodsInspections = Object.values(groupedByGoodsCode);

  return {
    ...data,
    returnOrder: {
      ...data.returnOrder,
      returnGoodsInspections: formattedReturnGoodsInspections,
    },
  };
};

/**
 * [교환 신청/조회] 교환 신청 상세 조회 api 요청 훅
 */
const useGetExchangeOrdersExchangeReceiptId = () => {
  const { runAsync } = useAsyncHandler();

  const getExchangeOrdersExchangeReceiptId = useAPI<
    TypeGetExchangeOrdersExchangeReceiptIdParams,
    TypeGetExchangeOrdersExchangeReceiptIdResponse
  >({
    endpoint: API_ENDPOINT.CLAIM.GET_EXCHANGE_ORDERS_EXCHANGE_RECEIPT_ID,
    method: 'get',
  });

  const requestExchangeOrdersDetail = async (
    { exchangeReceiptId }: TypeGetExchangeOrdersExchangeReceiptIdParams,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypeExchangeDetailData) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    }
  ) => {
    runAsync(() => getExchangeOrdersExchangeReceiptId({ pathParams: `/${exchangeReceiptId}` }), {
      onSuccess: ({ data }) => onSuccess?.(formatExchangeDetailData(data)),
      onError: (error: TypeErrorResponseData['error']) => onError?.(error),
    });
  };

  return { requestExchangeOrdersDetail };
};

export default useGetExchangeOrdersExchangeReceiptId;

const EXCHANGE_INSPECTION_IMAGE_URL = 'https://kurly-e2e.s3.ap-northeast-2.amazonaws.com/claim/inspection-02.jpg';

/** 검수 처리 상세는 상품코드로 묶인다. 첫 상품은 검수 2건, 둘째 상품은 1건. 교환 배송은 출고 전 상품(출고요청번호 없음)을 함께 둔다. */
const exchangeDetailResponse: TypeGetExchangeOrdersExchangeReceiptIdResponse = {
  data: {
    exchangeReceiptId: 'EX202605190003',
    returnOrder: {
      returnReceiptId: 'RT202605190013',
      clientOrderCode: 'CL-20260518-0003',
      outboundOrderCode: 'OB202605180003',
      sellerCode: 'SL0001',
      returnCourier: { code: 'LOTTE', name: '롯데' },
      status: { code: 'RETURN_INSPECTION_ING', name: '검수중' },
      totalBox: 1,
      member: { name: '박배송', phoneNumber: '01012345678' },
      returnPlace: {
        primaryAddress: '서울특별시 강남구 테헤란로 133',
        secondaryAddress: '컬리타워 10층',
        accessMethod: '공동현관 비밀번호',
        accessDetail: '#1234',
        pickupType: '문 앞',
      },
      returnOrderTrackings: [
        {
          invoiceNumber: '250098763001',
          status: { code: 'COURIER_DELIVERED_COMPLETE', name: '회수완료' },
          statusAt: '2026-05-19 07:45:00',
          failureReason: '',
          invoiceTrackingUrl: 'https://tracking.example.com/250098763001',
        },
      ],
      returnGoodsInspections: [
        {
          goodsCode: 'G00000003',
          goodsName: '[컬리] 교환 대상 상품 3',
          returnReason: { code: 'DEFECT', name: '상품불량' },
          inboundStatus: '입하완료',
          returnQuantity: 2,
          inspectionQuantity: 1,
          inspectionStatus: '검수성공',
          inspectionAt: '2026-05-19 08:20:00',
          unlistedYn: false,
          inspectionImageUrls: [EXCHANGE_INSPECTION_IMAGE_URL],
        },
        {
          goodsCode: 'G00000003',
          goodsName: '[컬리] 교환 대상 상품 3',
          returnReason: { code: 'DEFECT', name: '상품불량' },
          inboundStatus: '입하불가',
          returnQuantity: 2,
          inspectionQuantity: 1,
          inspectionStatus: '검수실패',
          inspectionAt: '2026-05-19 08:22:00',
          unlistedYn: false,
          inspectionImageUrls: [EXCHANGE_INSPECTION_IMAGE_URL],
        },
        {
          goodsCode: 'G00000021',
          goodsName: '[컬리] 동봉 상품',
          returnReason: { code: 'WRONG_DELIVERY', name: '오배송' },
          inboundStatus: '입하전',
          returnQuantity: 1,
          inspectionQuantity: 0,
          inspectionStatus: '검수전',
          inspectionAt: null,
          unlistedYn: true,
          inspectionImageUrls: [],
        },
      ],
      returnReceiptAt: '2026-05-19 08:23:00',
    },
    exchangeOrder: {
      exchangeOrderItems: [
        {
          outboundOrderCode: 'OB202605191003',
          goodsCode: 'G00000003',
          goodsName: '[컬리] 교환 대상 상품 3',
          quantity: 1,
        },
        { outboundOrderCode: null, goodsCode: 'G00000031', goodsName: '[컬리] 대체 배송 상품', quantity: 1 },
      ],
    },
  },
};

registerMockAPI<TypeGetExchangeOrdersExchangeReceiptIdResponse>({
  method: 'get',
  // 숫자가 하나 이상 들어간 요청번호만 받아 `/exchange-orders/existence` 를 가로채지 않는다.
  endpoint: '/api-oms/exchange-orders/:exchangeReceiptId([A-Z]{0,}\\d[A-Z0-9]{0,})',
  label: '반품교환/교환신청조회/상세',
  scenarios: [
    { name: '정상 응답 — 검수 2개 상품(3건)·교환 배송 2건', response: exchangeDetailResponse },
    {
      name: '서버 에러 (500)',
      response: { error: { code: 500, message: '교환 상세를 불러오지 못했습니다.' } },
      status: 500,
    },
  ],
});
